import { useCallback, useEffect, useState, type RefObject } from "react";
import { Platform } from "react-native";
import type { View } from "react-native";
import { colors } from "../styles/design-system";
import { ensureMathLive } from "../utils/mathLive";

/** Sets MathLive CSS custom properties on an element for virtual keyboard theming. */
function setMathVirtualKeyboardThemeOnElement(el: HTMLElement): void {
  const s = el.style;
  s.setProperty("--keyboard-background", colors.grey[50]);
  s.setProperty("--keyboard-toolbar-background", colors.grey[50]);
  s.setProperty("--keyboard-toolbar-text", colors.grey[700]);
  s.setProperty("--keyboard-toolbar-text-active", colors.brand[600]);
  s.setProperty("--keyboard-toolbar-background-hover", colors.grey[100]);
  s.setProperty("--keyboard-accent-color", colors.brand[500]);
  s.setProperty("--keyboard-border", `1px solid ${colors.grey[200]}`);
  s.setProperty("--keyboard-horizontal-rule", colors.grey[200]);
  s.setProperty("--keycap-background", colors.white);
  s.setProperty("--keycap-background-hover", colors.grey[200]);
  s.setProperty("--keycap-background-active", colors.grey[200]);
  s.setProperty("--keycap-background-pressed", colors.brand[100]);
  s.setProperty("--keycap-border", colors.grey[200]);
  s.setProperty("--keycap-border-bottom", colors.grey[300]);
  s.setProperty("--keycap-text", colors.grey[800]);
  s.setProperty("--keycap-text-active", colors.grey[900]);
  s.setProperty("--keycap-text-hover", colors.grey[900]);
  s.setProperty("--keycap-text-pressed", colors.brand[700]);
  s.setProperty("--keycap-shift-text", colors.grey[500]);
  s.setProperty("--keycap-shift-color", colors.brand[500]);
  s.setProperty("--keycap-primary-background", colors.brand[500]);
  s.setProperty("--keycap-primary-text", "#fff");
  s.setProperty("--keycap-primary-background-hover", colors.brand[600]);
  s.setProperty("--keycap-secondary-background", colors.grey[100]);
  s.setProperty("--keycap-secondary-background-hover", colors.grey[200]);
  s.setProperty("--keycap-secondary-text", colors.grey[700]);
  s.setProperty("--keycap-secondary-border", colors.grey[200]);
  s.setProperty("--keycap-secondary-border-bottom", colors.grey[300]);
  s.setProperty("--variant-panel-background", colors.grey[50]);
  s.setProperty("--variant-keycap-text", colors.grey[700]);
  s.setProperty("--variant-keycap-text-active", colors.grey[900]);
  s.setProperty("--variant-keycap-background-active", colors.brand[100]);
}

/**
 * Applies design-system theme to the MathLive virtual keyboard.
 * Call when attaching the keyboard (vk.container = el).
 * Hides the edit toolbar (undo, redo, paste) in the virtual keyboard.
 */
const KEYBOARD_TOP_SHADOW = "0 -4px 12px rgba(16, 24, 40, 0.08)";

export function applyMathVirtualKeyboardTheme(container: HTMLElement): void {
  setMathVirtualKeyboardThemeOnElement(container);
  container.style.boxShadow = KEYBOARD_TOP_SHADOW;
  if (typeof document !== "undefined") {
    if (document.body) setMathVirtualKeyboardThemeOnElement(document.body);
  }
  const vk = getMathVirtualKeyboard();
  if (vk && "editToolbar" in vk) {
    vk.editToolbar = "none";
  }
}

/** Shape of the MathLive global mathVirtualKeyboard (window.mathVirtualKeyboard). */
interface MathVirtualKeyboard {
  container: HTMLElement | null;
  show: (options?: { animate?: boolean }) => void;
  hide: (options?: { animate?: boolean }) => void;
  visible: boolean;
  boundingRect?: DOMRect;
  /** "default" | "none" – when "none", the edit toolbar (undo, redo, paste) is hidden. */
  editToolbar?: string;
  addEventListener?: (event: string, handler: () => void) => void;
  removeEventListener?: (event: string, handler: () => void) => void;
}

/** Returns the MathLive global virtual keyboard API (web only). */
function getMathVirtualKeyboard(): MathVirtualKeyboard | null {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  return (window as Window & { mathVirtualKeyboard?: MathVirtualKeyboard }).mathVirtualKeyboard ?? null;
}

const VK_ANIMATE = { animate: true } as const; // MathLive show/hide option

/**
 * Manages the MathLive virtual keyboard: attach to a container ref, apply design-system theme,
 * sync height on geometry changes, and expose a toggle. Used by MathVirtualKeyboardProvider
 * so that ChatComposerBar and others consume keyboard state via context instead of props.
 *
 * @param containerRef - Ref to the DOM element that hosts the keyboard (provider’s container View).
 * @param open - Whether the keyboard should be visible.
 * @param setOpen - State setter for open (toggle flips it).
 * @param enabled - When false, effects do not run (keyboard not attached). Default true.
 * @returns toggleVirtualKeyboard - Call to flip open state (no-op on non-web).
 */
export function useMathVirtualKeyboard({
  containerRef,
  open,
  setOpen,
  enabled = true,
}: {
  containerRef: RefObject<View | null>;
  open: boolean;
  setOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  enabled?: boolean;
}): { toggleVirtualKeyboard: () => void } {
  const vk = getMathVirtualKeyboard();
  /** Incremented when MathLive becomes ready while open; triggers main effect to attach keyboard. */
  const [vkReadyTick, setVkReadyTick] = useState(0);

  /** When open but vk not ready yet (MathLive loading async), subscribe to the same ready promise
   * so we re-run the attach effect once window.mathVirtualKeyboard is available. */
  useEffect(() => {
    if (!enabled || Platform.OS !== "web" || !open || vk) return;
    let cancelled = false;
    ensureMathLive().then(() => {
      if (!cancelled) setVkReadyTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, open, vk]);

  /** Sync container height to MathLive's reported keyboard height (e.g. after geometrychange). */
  const applyKeyboardHeight = useCallback((el: HTMLElement, keyboard: MathVirtualKeyboard) => {
    const rect = keyboard.boundingRect;
    if (rect && rect.height > 0) {
      el.style.height = `${rect.height}px`;
    }
  }, []);

  /** When open: attach keyboard to container, theme it, show it, sync height. When closed: detach and hide. */
  useEffect(() => {
    if (!enabled || Platform.OS !== "web" || !vk) return;
    if (open) {
      const el = containerRef.current as unknown as HTMLElement | null;
      if (el) {
        vk.container = el;
        applyMathVirtualKeyboardTheme(el);
        vk.show(VK_ANIMATE);
        const onGeometryChange = () => applyKeyboardHeight(el, vk);
        vk.addEventListener?.("geometrychange", onGeometryChange);
        requestAnimationFrame(() => applyKeyboardHeight(el, vk));
        return () => {
          vk.removeEventListener?.("geometrychange", onGeometryChange);
        };
      }
    } else {
      const el = containerRef.current as unknown as HTMLElement | null;
      if (el) el.style.height = "";
      vk.container = null;
      vk.hide(VK_ANIMATE);
    }
  }, [enabled, open, vk, vkReadyTick, applyKeyboardHeight, containerRef]);

  /** On unmount or when enabled becomes false: detach keyboard and hide it. */
  useEffect(() => {
    if (!enabled) return;
    return () => {
      if (Platform.OS === "web" && vk) {
        vk.container = null;
        vk.hide(VK_ANIMATE);
      }
    };
  }, [enabled, vk]);

  const toggleVirtualKeyboard = useCallback(() => {
    if (Platform.OS !== "web") return;
    setOpen((prev) => !prev);
  }, [setOpen]);

  return { toggleVirtualKeyboard };
}
